import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import { useAuth } from '@healthdesk/shared-hooks';
import { contentCardSx } from '../../../styles/standaloneLayoutTokens';
import { usePendingDispatches } from '../hooks/usePendingDispatches';

function formatExpiry(expiresAt) {
  if (!expiresAt) return '';
  const date = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const PendingConversationsCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { dispatches, loading, error } = usePendingDispatches(user?.uid);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ ...contentCardSx, mb: 2 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Checking for pending conversations...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        We could not load pending conversations right now.
      </Alert>
    );
  }

  if (!dispatches.length) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ ...contentCardSx, mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ChatOutlinedIcon color="primary" />
          <Typography variant="h6">Conversations To Complete</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your care team sent these short health conversations for you.
        </Typography>
        <Stack spacing={1.5}>
          {dispatches.map((dispatch) => (
            <Box
              key={dispatch.dispatchId || dispatch.id}
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle2">
                  {dispatch.templateSnapshot?.title || 'Health conversation'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                  <Chip size="small" label={dispatch.status === 'started' ? 'Started' : 'New'} />
                  {formatExpiry(dispatch.expiresAt) ? (
                    <Chip size="small" variant="outlined" label={`Expires ${formatExpiry(dispatch.expiresAt)}`} />
                  ) : null}
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate(`/conversation/${dispatch.dispatchId || dispatch.id}/${dispatch.code}`)}
              >
                Open
              </Button>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PendingConversationsCard;
